import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EMPTY, catchError, filter, fromEvent, map, merge, of, switchMap, take, timer } from 'rxjs';
import { exhaustMap } from 'rxjs/operators';
import { apiErrorSnackKey, readApiError } from 'app/core/api-error';
import { CurrentContextService } from 'app/core/current-context.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { CommunityNotificationsService } from '../../services/community-notifications.service';
import { CommunityNotification } from '../../services/models/community-notifications.models';
import { PANEL_TAKE, badgeText, notificationRow, NotificationRow } from '../notifications.models';
import { NotificationPanelState } from '../notification-panel/notification-panel.component';
import { WebPushService } from '../web-push.service';

// Five minutes, not one: the bell mounts for every signed-in session and each tick reaches Postgres.
export const UNREAD_POLL_MS = 300_000;

/**
 * The bell in the top bar, and the owner of everything stateful in this feature.
 *
 * It owns the writes rather than the panel because a row click both navigates and closes the menu, and the
 * panel is destroyed on close: a `markSeen` carrying `takeUntilDestroyed` from the panel would be aborted by
 * the very click that started it, leaving the row unread for good. This component lives in the top bar for
 * the whole session and is never destroyed by a click.
 */
@Component({
  selector: 'app-knowledge-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NotificationBellComponent implements OnInit {
  private readonly notifications = inject(CommunityNotificationsService);
  private readonly webPush = inject(WebPushService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly items = signal<readonly CommunityNotification[]>([]);
  private readonly feedLoading = signal(false);
  private readonly feedError = signal(false);
  private readonly marking = signal(false);
  /** Ticked by the poll, so relative timestamps refresh on the same beat without a second timer. */
  private readonly now = signal(Date.now());
  /** A plain field: only the poll's filter reads it, and no template does. */
  private panelIsOpen = false;

  public readonly unreadCount = signal(0);
  public readonly badgeHidden = computed(() => this.unreadCount() === 0);
  public readonly badgeLabel = computed(() => badgeText(this.unreadCount()));
  public readonly hasUnread = computed(() => this.unreadCount() > 0);
  public readonly bellLabelKey = computed(() =>
    this.hasUnread() ? 'knowledge.notifications.bell-unread' : 'knowledge.notifications.bell',
  );
  public readonly bellLabelParams = computed(() => ({ count: this.unreadCount() }));

  public readonly panelState = computed<NotificationPanelState>(() => ({
    rows: this.items().map((item) => notificationRow(item, this.now())),
    loading: this.feedLoading(),
    hasError: this.feedError(),
    unreadCount: this.unreadCount(),
    canMarkAll: this.unreadCount() > 0 && !this.marking(),
    push: this.webPush.state(),
    pushPending: this.webPush.pending(),
  }));

  constructor() {
    // timer(0, ...) not interval(...): interval waits a full minute, so the badge would be blank on first
    // paint. The gate rebuilds the timer on return rather than unblocking a filter, which is what makes a
    // return instant.
    merge(of(null), fromEvent(this.document, 'visibilitychange'))
      .pipe(
        map(() => !this.document.hidden),
        switchMap((visible) => (visible ? timer(0, UNREAD_POLL_MS) : EMPTY)),
        // While the panel is open the feed's own unreadCount owns the number, and while a write settles the
        // local patch does. Two reads of two different instants would disagree in one badge.
        filter(() => !this.panelIsOpen && !this.marking()),
        // exhaustMap so a duplicate trigger is dropped rather than cancelling an answer already in flight. The
        // catch sits inside the projection: outside it, one offline minute would end the poll for the session.
        exhaustMap(() => this.notifications.getUnreadCount().pipe(catchError(() => EMPTY))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.now.set(Date.now());
          this.unreadCount.set(result.unreadCount);
        },
      });

    this.webPush.clicked$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((eventId) => this.openFromWorker(eventId));

    this.webPush.pushed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refreshAfterPush());
  }

  public ngOnInit(): void {
    // A BehaviorSubject, so a bell built after the identity round trip still receives it, and a defined value
    // proves both signed in and identity known. The top bar is rebuilt on every fullscreen navigation, which
    // is why syncOnAppLoad has to be idempotent.
    this.currentContextService.currentContextBS
      .pipe(
        filter((context) => !!context?.user),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.webPush.syncOnAppLoad());
  }

  public onOpened(): void {
    this.panelIsOpen = true;
    this.loadFeed();
  }

  public onClosed(): void {
    this.panelIsOpen = false;
  }

  public loadFeed(): void {
    this.feedLoading.set(true);
    this.feedError.set(false);

    this.notifications
      .getFeed(0, PANEL_TAKE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (feed) => {
          this.feedLoading.set(false);
          this.now.set(Date.now());
          this.items.set(feed.items ?? []);
          this.unreadCount.set(feed.unreadCount);
        },
        error: (error: unknown) => {
          this.feedLoading.set(false);
          this.feedError.set(true);
          this.snackBarService.error(apiErrorSnackKey(readApiError(error)));
        },
      });
  }

  public onActivated(row: NotificationRow): void {
    if (row.item.seen) {
      return;
    }

    this.patchSeen(row.item.id, true);
    this.unreadCount.update((count) => Math.max(0, count - 1));

    this.notifications
      .markSeen(row.item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (error: unknown) => {
          this.patchSeen(row.item.id, false);
          this.unreadCount.update((count) => count + 1);
          this.snackBarService.error(apiErrorSnackKey(readApiError(error)));
        },
      });
  }

  public onMarkAll(): void {
    if (!this.panelState().canMarkAll) {
      return;
    }

    const previousItems = this.items();
    const previousCount = this.unreadCount();

    this.marking.set(true);
    this.items.set(previousItems.map((item) => (item.seen ? item : { ...item, seen: true })));
    this.unreadCount.set(0);

    this.notifications
      .markAllSeen()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.marking.set(false),
        error: (error: unknown) => {
          this.marking.set(false);
          this.items.set(previousItems);
          this.unreadCount.set(previousCount);
          this.snackBarService.error(apiErrorSnackKey(readApiError(error)));
        },
      });
  }

  public onPushToggle(): void {
    void (this.webPush.canDisable() ? this.webPush.disable() : this.webPush.enable());
  }

  /** The worker focused an open tab rather than opening one, so this window does the routing. */
  private openFromWorker(eventId: string): void {
    void this.router.navigate(eventId ? ['/knowledge/notifications', eventId] : ['/knowledge/notifications']);
  }

  private refreshAfterPush(): void {
    if (this.panelIsOpen) {
      this.loadFeed();
      return;
    }

    this.notifications
      .getUnreadCount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (result) => this.unreadCount.set(result.unreadCount) });
  }

  private patchSeen(id: string, seen: boolean): void {
    this.items.update((current) => current.map((item) => (item.id === id ? { ...item, seen } : item)));
  }
}
